<template>
  <PageWrap :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
    <template #left>
      <BaseHeading size="s">
        {{ $t('treatwell.landing.preheading') }}
      </BaseHeading>
      <BaseHeading size="xl">{{ averageBookingsPerMonth }}</BaseHeading>
      <BaseText size="l">{{ $t('treatwell.landing.heading') }}</BaseText>
    </template>
    <template #cta>
      <Button />
    </template>
    <template #links>
      <BaseText
        v-if="!hasFeatureFlag('treatwell-brand')"
        :href="$t('treatwell.landing.link_video.url')"
        iconBefore="play-circle"
      >
        {{ $t('treatwell.landing.link_video.text') }}
      </BaseText>
      <BaseText
        :href="helpCenterArticleUrl($t('treatwell.landing.link_conditions.url'))"
        iconBefore="book-open"
      >
        {{ $t('treatwell.landing.link_conditions.text') }}
      </BaseText>
    </template>
    <template #right>
      <div :class="$style.calendarImage">
        <CalendarImage />
      </div>
    </template>
    <template #bottom>
      <Flex
        direction="column"
        :gap="1"
        align="center"
      >
        <transition
          name="fade-slide-5"
          appear
        >
          <BaseHeading
            size="s"
            mt
            mb
            :class="$style.reasonsHeader"
          >
            {{ $t('treatwell.landing.reasons.header') }}
          </BaseHeading>
        </transition>
        <div :class="$style.reasons">
          <div
            v-for="(reason, index) in reasons"
            :key="reason.id"
            :class="$style.reasonCardHolder"
          >
            <transition
              :name="`fade-slide-${index + 3}`"
              appear
            >
              <BaseCard
                v-if="!(company.activeSubscription?.plan === 'lite' && reason.id === 2)"
                hasHover
                fullWidth
                fullHeight
              >
                <Flex
                  direction="column"
                  :gap="1"
                  :class="$style.reasonCard"
                >
                  <img
                    :src="`/img/treatwell/${reason.icon}.svg`"
                    :class="$style.reasonIcon"
                    height="25px"
                    width="25px"
                  >
                  <BaseText
                    bold
                    :size="reason.id === 2 ? 'l' : 'm'"
                  >
                    {{ reason.title }}
                  </BaseText>
                  <BaseText color="secondary">{{ reason.text }}</BaseText>
                </Flex>
              </BaseCard>
            </transition>
          </div>
        </div>
      </Flex>
    </template>
  </PageWrap>
</template>

<script
  setup
  lang="ts"
>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandName } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import PageWrap from '@/modules/online/PageWrap.vue';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import Button from './Button.vue';
import CalendarImage from './CalendarImage.vue';

const { t } = useI18n();

const { company } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();

const averageBookingsPerMonth = computed(() => {
  switch (company.companyType) {
    case 'BARBERSHOP':
      return t('treatwell.landing.salon_specific.barbershop.more_bookings_2');
    case 'NAIL_STUDIO':
    case 'MANI_PEDICURE':
      return t('treatwell.landing.salon_specific.nail_studio.more_bookings_2');
    default:
      return t('treatwell.landing.more_bookings_2');
  }
});

const reasons = [
  {
    id: 2,
    title: t('treatwell.landing.reasons.reason_2'),
    text: t('treatwell.landing.reasons.reason_2_text'),
    icon: 'money',
  },
  {
    id: 1,
    title: t('treatwell.landing.reasons.reason_1'),
    text: t('treatwell.landing.reasons.reason_1_text'),
    icon: 'graph',
  },
  {
    id: 3,
    title: t('treatwell.landing.reasons.reason_3'),
    text: t('treatwell.landing.reasons.reason_3_text', brandName()),
    icon: 'diamond',
  },
];
</script>

<style
  lang="scss"
  module
>
.reasonsHeader {
  .base:global(.v-enter-active) & {
    transition: opacity 0.2s linear;
  }
  .base:global(.v-enter-from) & {
    opacity: 0;
  }
}
.reasons {
  display: flex;
  gap: $spacing;
  flex-wrap: wrap;
  justify-content: space-between;

  .base.smallScreen & {
    flex-direction: column;
    justify-content: center;
  }
}

.reasonCardHolder {
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: calc(50% - $spacing);

  .base:not(.mediumScreen):not(.smallScreen) & {
    flex-basis: calc(33.33% - $spacing);
  }

  .base.mediumScreen & {
    flex-basis: calc(50% - $spacing);
  }
  .base.smallScreen & {
    flex-basis: calc(100% - $spacing);
  }
}

.reasonCard {
  padding: 0.5 * $spacing;
  .base.mediumScreen & {
    padding: 0.5 * $spacing 0.25 * $spacing;
  }
  .base.smallScreen & {
    padding: 0.5 * $spacing 0.25 * $spacing;
  }
}

.reasonIcon {
  .reasonCard:hover & {
    transform: scale(1.1);
  }
  transition: transform 0.3s $easeInOutCubic;
}

.calendarImage {
  width: 100%;
}
</style>
